import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useBooking } from '../../../../context/contexts';
import { formatTimeDisplay } from '../../../../lib/utils/dateTimeFormat';
import './UserBookings.css';

const UserBookings = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { 
        bookings, 
        loading, 
        error, 
        setSelectedCar, 
        setSelectedService, 
        setSelectedAddOns,
        updateStatus
    } = useBooking();
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [isModifying, setIsModifying] = useState(false);
  
    const handleCreateBooking = () => {
      navigate('/booking');
    };
  
    const handleModifyBooking = (booking) => {
      setSelectedBooking(booking);
      setIsModifying(true);
  
      // Pre-fill booking data
      setSelectedCar(booking.vehicle_types);
      setSelectedService(booking.services);
      setSelectedAddOns(booking.booking_addons?.map(addon => addon.service_addons) || []);
  
      navigate('/booking');
    };
  
    const handleCancelBooking = async (bookingId) => {
      if (window.confirm('Are you sure you want to cancel this booking?')) {
        try {
          // Use the context function to update status to 'Cancelled'
          await updateStatus(bookingId, 'Cancelled');
        } catch (error) {
          console.error('Error cancelling booking:', error);
        }
      }
    };
  
    const getStatusClass = (status) => {
      switch (status?.toLowerCase()) {
        case 'completed':
          return 'status-completed';
        case 'cancelled':
          return 'status-cancelled';
        case 'pending':
          return 'status-pending';
        case 'confirmed':
          return 'status-confirmed';
        default:
          return '';
      }
    };

    // Format the addons as a comma-separated list
    const formatAddons = (booking) => {
      if (!booking.booking_addons || booking.booking_addons.length === 0) {
        return '';
      }
      
      return booking.booking_addons
        .map(addon => addon.service_addons?.name)
        .filter(Boolean)
        .join(', ');
    };

    if (loading) {
      return <div className="loading">Loading your bookings...</div>;
    }

    if (error) {
      return <div className="error">Error loading bookings: {error}</div>;
    }

    return (
        <div className="user-bookings">
            <div className="user-bookings__card-header">
                <h2 className="user-bookings__card-title">My Bookings</h2>
                <button
                    className="user-bookings__create-booking"
                    onClick={handleCreateBooking}
                >
                    Create New Booking
                </button>
            </div>

            <div className="user-bookings__bookings">
                {bookings && bookings.length > 0 ? (
                    <div className="user-bookings__table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Service & Add-ons</th>
                                    <th>Vehicle</th>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings.map(booking => (
                                    <tr key={booking.id}>
                                        <td>
                                            <div className="service-name">{booking.services?.name || 'N/A'}</div>
                                            {formatAddons(booking) && (
                                                <div className="service-addons">
                                                    <span className="addons-label">Add-ons:</span> {formatAddons(booking)}
                                                </div>
                                            )}
                                        </td>
                                        <td>{booking.vehicle_types?.title || 'N/A'}</td>
                                        <td>{booking.booking_date || 'N/A'}</td>
                                        <td>{formatTimeDisplay(booking.booking_time)}</td>
                                        <td>
                                            <span className={`status-badge ${getStatusClass(booking.status)}`}>
                                                {booking.status || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            {(booking.status === 'Pending' || booking.status === 'Confirmed') && (
                                                <div className="user-bookings__actions">
                                                    <button
                                                        className="user-bookings__action-btn user-bookings__action-btn--modify"
                                                        onClick={() => handleModifyBooking(booking)}
                                                    >
                                                        Modify
                                                    </button>
                                                    <button
                                                        className="user-bookings__action-btn user-bookings__action-btn--cancel"
                                                        onClick={() => handleCancelBooking(booking.id)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="user-bookings__no-bookings">
                        You don't have any bookings yet.
                        <button onClick={handleCreateBooking}>Create your first booking</button>
                    </p>
                )}
            </div>
        </div>
    );
};

export default UserBookings;